import {
  ProcessorWrapper,
  type ProcessorWrapperOptions,
  type SegmenterOptions,
  type SwitchBackgroundProcessorOptions,
} from '@livekit/track-processors'
import {
  StabilizedBackgroundTransformer,
  type StabilizedBackgroundOptions,
} from '@toolify/client/src/modules/call/processors/StabilizedBackgroundTransformer'
import type {IStabilizedMaskTuning} from '@toolify/client/src/modules/call/processors/setupStabilizedBackgroundGL'

const DEFAULT_BLUR_RADIUS = 10

export type StabilizedBackgroundProcessorOptions = SwitchBackgroundProcessorOptions &
  ProcessorWrapperOptions & {
    segmenterOptions?: SegmenterOptions
    assetPaths?: {
      tasksVisionFileSet?: string
      modelAssetPath?: string
    }
    onFrameProcessed?: StabilizedBackgroundOptions['onFrameProcessed']
    maskTuning?: Partial<IStabilizedMaskTuning>
  }

export class StabilizedBackgroundProcessorWrapper extends ProcessorWrapper<
  StabilizedBackgroundOptions,
  StabilizedBackgroundTransformer
> {
  async switchTo(options: SwitchBackgroundProcessorOptions) {
    switch (options.mode) {
    case 'background-blur':
      await this.updateTransformerOptions({
        imagePath: undefined,
        blurRadius: options.blurRadius ?? DEFAULT_BLUR_RADIUS,
        backgroundDisabled: false,
      })
      break
    case 'virtual-background':
      await this.updateTransformerOptions({
        imagePath: options.imagePath,
        blurRadius: undefined,
        backgroundDisabled: false,
      })
      break
    case 'disabled':
      await this.updateTransformerOptions({
        imagePath: undefined,
        blurRadius: undefined,
        backgroundDisabled: true,
      })
      break
    default: {
      const exhaustiveCheck: never = options
      throw new Error(`Unhandled background processor mode: ${String(exhaustiveCheck)}`)
    }
    }
  }
}

export function supportsStabilizedBackgroundProcessor() {
  return StabilizedBackgroundTransformer.isSupported && ProcessorWrapper.isSupported
}

export function createStabilizedBackgroundProcessor(
  options: StabilizedBackgroundProcessorOptions,
  name = 'stabilized-background-processor',
) {
  if (!StabilizedBackgroundTransformer.isSupported) {
    throw new Error('Background transformer is not supported in this browser')
  }
  if (!ProcessorWrapper.isSupported) {
    throw new Error(
      'Neither MediaStreamTrackProcessor nor canvas.captureStream() fallback is supported in this browser',
    )
  }
  const {segmenterOptions, assetPaths, onFrameProcessed, maskTuning, maxFps} = options
  const transformerOptions: StabilizedBackgroundOptions = {
    segmenterOptions,
    assetPaths,
    onFrameProcessed,
    maskTuning,
  }
  if (options.mode === 'background-blur') {
    transformerOptions.blurRadius = options.blurRadius ?? DEFAULT_BLUR_RADIUS
  }
  if (options.mode === 'virtual-background') {
    transformerOptions.imagePath = options.imagePath
  }
  const transformer = new StabilizedBackgroundTransformer(transformerOptions)
  return new StabilizedBackgroundProcessorWrapper(transformer, name, {maxFps})
}
